// Content for the mechanical engineering learning portal: areas → topics.
// Each topic has concepts to tick off (they drive the progress bars),
// core formulas, common pitfalls, a few self-test questions and resources.
// Every resource URL was checked when it was added.

export type Level = "grund" | "karna" | "fordjup";
export type ResKind = "kurs" | "bok" | "video" | "verktyg" | "text";

export interface Formula { name: string; expr: string; note?: string }
export interface Resource { title: string; kind: ResKind; url?: string; note: string }
export interface Quiz { q: string; a: string }
export interface Topic {
  id: string;
  title: string;
  level: Level;
  summary: string;   // one line for the overview card
  why: string;       // why it matters in practice
  concepts: string[];
  formulas: Formula[];
  pitfalls: string[];
  quiz: Quiz[];
  resources: Resource[];
}
export interface Area { id: string; title: string; topics: Topic[] }

export const LEVELS: Record<Level, string> = {
  grund: "Grund",
  karna: "Kärna",
  fordjup: "Fördjupning",
};

export const KINDS: Record<ResKind, string> = {
  kurs: "Kurs",
  bok: "Bok",
  video: "Video",
  verktyg: "Verktyg",
  text: "Text",
};

const EFFICIENT = "https://www.youtube.com/@TheEfficientEngineer";
const OCW = (slug: string) => `https://ocw.mit.edu/courses/${slug}/`;

export const AREAS: Area[] = [
  {
    id: "grund",
    title: "Grunderna",
    topics: [
      {
        id: "matematik",
        title: "Matematik för ingenjörer",
        level: "grund",
        summary: "Analys, linjär algebra och differentialekvationer: språket allt annat skrivs i.",
        why: "Nästan varje modell du gör, från en balk till en reglerloop, är en ekvation eller ett ekvationssystem. Du behöver inte vara matematiker, men du måste kunna ställa upp, förenkla och tolka.",
        concepts: [
          "Derivator och integraler, även i flera variabler",
          "Taylorutveckling och linjärisering",
          "Vektorer, matriser och ekvationssystem (Ax = b)",
          "Egenvärden och egenvektorer",
          "Linjära ODE av första och andra ordningen",
          "Enhetsanalys och rimlighetskontroll",
          "Grundläggande statistik: medelvärde, standardavvikelse, normalfördelning",
        ],
        formulas: [
          { name: "Linjärisering", expr: "f(x + h) ≈ f(x) + f′(x)·h", note: "Grunden för nästan alla ingenjörsapproximationer" },
          { name: "Partiell integration", expr: "∫u dv = u·v − ∫v du" },
          { name: "Ekvationssystem", expr: "A·x = b  →  x = A⁻¹·b", note: "Lös numeriskt, invertera aldrig i praktiken" },
          { name: "Egenvärdesproblem", expr: "A·v = λ·v", note: "Ger t.ex. egenfrekvenser och huvudspänningar" },
          { name: "Svängningsekvationen", expr: "m·x″ + c·x′ + k·x = F(t)" },
        ],
        pitfalls: [
          "Att räkna utan enheter. Bär med dem genom hela uträkningen, så hittar du felen själv.",
          "Att lita på ett svar du inte har uppskattat i förväg. Gör alltid en snabb överslagsräkning.",
        ],
        quiz: [
          { q: "Vad säger egenvärdena för styvhets- och massmatrisen i ett mekaniskt system?", a: "De ger systemets egenvinkelfrekvenser i kvadrat (ω²), och egenvektorerna ger svängningsformerna." },
          { q: "Varför linjäriserar man runt en arbetspunkt?", a: "För att få en linjär modell som går att lösa och analysera, och som stämmer bra så länge man håller sig nära arbetspunkten." },
          { q: "Vad är derivatan av sin(ωt) med avseende på t?", a: "ω·cos(ωt). Därför är hastighetsamplituden i en svängning ω gånger lägesamplituden." },
        ],
        resources: [
          { title: "3Blue1Brown: Essence of linear algebra", kind: "video", url: "https://www.3blue1brown.com/topics/linear-algebra", note: "Bäst för att förstå vad matriser gör, inte bara räkna" },
          { title: "MIT 18.06SC Linear Algebra", kind: "kurs", url: OCW("18-06sc-linear-algebra-fall-2011"), note: "Gilbert Strangs klassiska kurs" },
          { title: "MIT 18.03SC Differential Equations", kind: "kurs", url: OCW("18-03sc-differential-equations-fall-2011"), note: "ODE med många ingenjörsexempel" },
          { title: "Khan Academy: Multivariable calculus", kind: "kurs", url: "https://www.khanacademy.org/math/multivariable-calculus", note: "Bra repetition i egen takt" },
        ],
      },
      {
        id: "statik",
        title: "Statik",
        level: "grund",
        summary: "Krafter och moment i jämvikt: frilägg, ställ upp, lös.",
        why: "All dimensionering börjar med att veta vilka krafter som verkar. En felaktig frilägging gör resten av beräkningen värdelös, hur avancerad den än är.",
        concepts: [
          "Frilägga (free body diagram)",
          "Kraftjämvikt och momentjämvikt i 2D och 3D",
          "Stödreaktioner: fast inspänning, led, rullstöd",
          "Fackverk: knutpunktsmetoden och snittmetoden",
          "Friktion (Coulomb)",
          "Tyngdpunkt och yttröghetsmoment",
          "Inre krafter: normalkraft, tvärkraft, böjmoment",
        ],
        formulas: [
          { name: "Jämvikt", expr: "ΣF = 0,  ΣM = 0" },
          { name: "Moment", expr: "M = r × F,  |M| = F·d", note: "d = vinkelrätt avstånd till kraftens verkningslinje" },
          { name: "Friktion", expr: "F ≤ μ·N", note: "Likhet först när det precis glider" },
          { name: "Tyngdpunkt", expr: "x̄ = Σ(Aᵢ·xᵢ) / ΣAᵢ" },
          { name: "Balk: samband", expr: "dV/dx = −q,  dM/dx = V", note: "Teckenkonventionen varierar mellan böcker" },
        ],
        pitfalls: [
          "Att glömma en stödreaktion, eller att räkna med ett moment i en led.",
          "Att blanda teckenkonventioner mellan tvärkrafts- och momentdiagram.",
        ],
        quiz: [
          { q: "En fritt upplagd balk, 2 m lång, har en punktlast på 1 kN i mitten. Hur stort är det största böjmomentet?", a: "M = F·L/4 = 1000·2/4 = 500 Nm, mitt på balken." },
          { q: "Hur många okända kan du lösa ut från en friläggning i planet?", a: "Tre: två kraftekvationer och en momentekvation. Har du fler okända är systemet statiskt obestämt." },
          { q: "En låda på 50 kg står på ett golv med μ = 0,4. Hur stor vågrät kraft krävs för att den ska börja glida?", a: "F = μ·m·g = 0,4·50·9,81 ≈ 196 N." },
        ],
        resources: [
          { title: "Engineering Statics: Open and Interactive", kind: "text", url: "https://engineeringstatics.org", note: "Gratis interaktiv lärobok" },
          { title: "Mechanics Map (Penn State)", kind: "text", url: "https://mechanicsmap.psu.edu", note: "Statik och dynamik med exempel och videor" },
          { title: "MIT 8.01SC Classical Mechanics", kind: "kurs", url: OCW("8-01sc-classical-mechanics-fall-2016"), note: "Fysikgrunden under statik och dynamik" },
        ],
      },
    ],
  },
  {
    id: "mekanik",
    title: "Mekanik och hållfasthet",
    topics: [
      {
        id: "hallfasthet",
        title: "Hållfasthetslära",
        level: "karna",
        summary: "Spänning, töjning, böjning, vridning och knäckning: håller detaljen?",
        why: "Kärnan i maskinkonstruktion. Med handberäkningar på balkar, axlar och stänger dimensionerar du det mesta, och kan kontrollera om en FEM-analys är rimlig.",
        concepts: [
          "Spänning och töjning, Hookes lag",
          "Sträckgräns, brottgräns och säkerhetsfaktor",
          "Böjning av balkar och böjspänning",
          "Vridning av axlar",
          "Nedböjning av balkar (elementarfall)",
          "Fleraxliga spänningar, huvudspänningar och Mohrs cirkel",
          "Effektivspänning enligt von Mises",
          "Knäckning (Euler)",
          "Spänningskoncentrationer",
        ],
        formulas: [
          { name: "Normalspänning", expr: "σ = F / A" },
          { name: "Hookes lag", expr: "σ = E·ε,  ε = ΔL / L" },
          { name: "Förlängning", expr: "δ = F·L / (E·A)" },
          { name: "Böjspänning", expr: "σ = M·y / I,  σ_max = M / W", note: "W = I / y_max (böjmotstånd)" },
          { name: "Rektangel", expr: "I = b·h³ / 12" },
          { name: "Vridning, massiv axel", expr: "τ = T·r / J,  J = π·d⁴ / 32" },
          { name: "Konsolbalk, last i änden", expr: "δ = F·L³ / (3·E·I)" },
          { name: "Fritt upplagd, last i mitten", expr: "δ = F·L³ / (48·E·I)" },
          { name: "von Mises (böj + vrid)", expr: "σ_vM = √(σ² + 3·τ²)" },
          { name: "Eulerknäckning", expr: "P_cr = π²·E·I / L_k²", note: "L_k = knäcklängd, beror på infästningen" },
        ],
        pitfalls: [
          "Att använda brottgränsen när det är sträckgränsen som gäller, eller tvärtom.",
          "Att räkna med statisk hållfasthet för en detalj som belastas cykliskt. Då styr utmattningen.",
          "Att glömma knäckning för slanka tryckta stänger: de kan knäckas långt under sträckgränsen.",
        ],
        quiz: [
          { q: "Du fördubblar höjden på en rektangulär balk. Hur påverkas nedböjningen?", a: "I ökar med 2³ = 8, så nedböjningen blir en åttondel." },
          { q: "En stålstång (E = 210 GPa) med area 100 mm² och längd 1 m dras med 21 kN. Hur mycket förlängs den?", a: "δ = F·L/(E·A) = 21 000·1000/(210 000·100) = 1 mm." },
          { q: "Du halverar diametern på en massiv axel men behåller momentet. Hur ändras vridspänningen?", a: "τ = 16·T/(π·d³), så spänningen blir 2³ = 8 gånger högre." },
        ],
        resources: [
          { title: "MIT 2.001 Mechanics & Materials I", kind: "kurs", url: OCW("2-001-mechanics-materials-i-fall-2006"), note: "Grundkursen i hållfasthet" },
          { title: "The Efficient Engineer", kind: "video", url: EFFICIENT, note: "Mycket tydliga animerade förklaringar av spänning, böjning och knäckning" },
          { title: "Sundström: Handbok och formelsamling i hållfasthetslära (KTH)", kind: "bok", note: "Svensk standardreferens med elementarfall" },
          { title: "Björk: Formler och tabeller för mekanisk konstruktion", kind: "bok", note: "Den klassiska svenska formelsamlingen" },
        ],
      },
      {
        id: "dynamik",
        title: "Dynamik",
        level: "karna",
        summary: "Rörelse och krafter: kinematik, kinetik, energi och rörelsemängd.",
        why: "Allt som rör sig, som mekanismer, robotar och fordon, behöver dynamiken för att få fram krafter, effekter och accelerationer.",
        concepts: [
          "Partikelkinematik: läge, hastighet, acceleration",
          "Stelkroppskinematik och rotation",
          "Newtons andra lag för translation och rotation",
          "Masströghetsmoment och Steiners sats",
          "Arbete, energi och effekt",
          "Impuls och rörelsemängd, stötar",
          "Relativ rörelse och roterande referenssystem",
        ],
        formulas: [
          { name: "Newton II", expr: "ΣF = m·a,  ΣM = I·α" },
          { name: "Kinetisk energi", expr: "T = ½·m·v² + ½·I·ω²" },
          { name: "Effekt", expr: "P = F·v = T·ω" },
          { name: "Centripetalacceleration", expr: "aₙ = v² / r = ω²·r" },
          { name: "Impuls", expr: "∫F dt = Δ(m·v)" },
          { name: "Steiners sats", expr: "I = I_G + m·d²" },
        ],
        pitfalls: [
          "Att blanda ihop masströghetsmoment (kg·m²) med yttröghetsmoment (m⁴).",
          "Att glömma att effekt = moment × vinkelhastighet i rad/s, inte varv per minut.",
        ],
        quiz: [
          { q: "En motor ger 10 Nm vid 3000 varv/min. Vilken effekt är det?", a: "ω = 3000·2π/60 ≈ 314 rad/s, så P = 10·314 ≈ 3,1 kW." },
          { q: "Varför rullar en massiv cylinder snabbare nedför ett lutande plan än ett rör med samma massa och radie?", a: "Röret har större masströghetsmoment (m·r² mot ½·m·r²), så mer av lägesenergin går åt till rotation och mindre till translation." },
          { q: "En bil på 1500 kg bromsar från 90 km/h till stillastående. Hur mycket rörelseenergi måste bromsarna ta upp?", a: "v = 25 m/s, så T = ½·1500·25² ≈ 470 kJ, som blir värme i bromsarna." },
        ],
        resources: [
          { title: "MIT 2.003SC Engineering Dynamics", kind: "kurs", url: OCW("2-003sc-engineering-dynamics-fall-2011"), note: "Föreläsningar, uppgifter och lösningar" },
          { title: "Mechanics Map (Penn State)", kind: "text", url: "https://mechanicsmap.psu.edu", note: "Dynamikdelen med lösta exempel" },
          { title: "Meriam & Kraige: Engineering Mechanics, Dynamics", kind: "bok", note: "Standardboken på många svenska högskolor" },
        ],
      },
      {
        id: "vibrationer",
        title: "Vibrationer",
        level: "fordjup",
        summary: "Egenfrekvens, dämpning och resonans, och hur man undviker det.",
        why: "Vibrationer ger buller, utmattning och dålig precision. Den vanligaste orsaken är att en egenfrekvens ligger nära en störfrekvens från motorer, hjul eller fläktar.",
        concepts: [
          "Fri svängning: egenfrekvens",
          "Dämpning och dämpningskvot",
          "Påtvingad svängning och resonans",
          "Vibrationsisolering och transmissibilitet",
          "Flerfrihetsgradssystem och svängningsformer",
          "Obalans i roterande maskiner",
        ],
        formulas: [
          { name: "Egenvinkelfrekvens", expr: "ωₙ = √(k / m),  fₙ = ωₙ / 2π" },
          { name: "Dämpningskvot", expr: "ζ = c / (2·√(k·m))" },
          { name: "Dämpad frekvens", expr: "ω_d = ωₙ·√(1 − ζ²)" },
          { name: "Förstoring", expr: "X / X_st = 1 / √((1 − r²)² + (2·ζ·r)²)", note: "r = ω / ωₙ" },
          { name: "Isolering", expr: "Fungerar när r > √2", note: "Mjukare upphängning ger bättre isolering" },
        ],
        pitfalls: [
          "Att göra konstruktionen styvare för att få bort vibrationer, när det i stället flyttar egenfrekvensen närmare störningen.",
          "Att räkna utan dämpning nära resonans. Då blir svaret oändligt.",
        ],
        quiz: [
          { q: "En massa på 10 kg sitter på en fjäder med k = 40 kN/m. Vilken är egenfrekvensen?", a: "ωₙ = √(40 000/10) = 63,2 rad/s, så fₙ ≈ 10 Hz." },
          { q: "Hur får du bättre vibrationsisolering av en maskin på gummifötter?", a: "Gör fötterna mjukare (lägre k) så att egenfrekvensen hamnar långt under störfrekvensen, minst en faktor √2 under." },
          { q: "Vad händer med egenfrekvensen om massan fyrdubblas och fjädern är densamma?", a: "Den halveras, eftersom ωₙ = √(k/m)." },
        ],
        resources: [
          { title: "MIT 2.003SC Engineering Dynamics", kind: "kurs", url: OCW("2-003sc-engineering-dynamics-fall-2011"), note: "Andra halvan handlar om vibrationer" },
          { title: "Rao: Mechanical Vibrations", kind: "bok", note: "Standardboken i ämnet" },
          { title: "The Efficient Engineer", kind: "video", url: EFFICIENT, note: "Bra introduktion till resonans och dämpning" },
        ],
      },
      {
        id: "utmattning",
        title: "Utmattning och brott",
        level: "fordjup",
        summary: "Varför detaljer går sönder under spänningar långt under sträckgränsen.",
        why: "De flesta mekaniska haverier är utmattningsbrott. Allt som roterar, vibrerar eller belastas om och om igen måste dimensioneras mot utmattning.",
        concepts: [
          "Spänningsamplitud och medelspänning",
          "Wöhlerkurva (S–N) och utmattningsgräns",
          "Korrektionsfaktorer: yta, storlek, temperatur",
          "Haigh- och Goodmandiagram",
          "Kälverkan och anvisningskänslighet",
          "Kumulativ skada (Palmgren–Miner)",
          "Brottmekanik: spricktillväxt",
        ],
        formulas: [
          { name: "Medel och amplitud", expr: "σ_m = (σ_max + σ_min) / 2,  σ_a = (σ_max − σ_min) / 2" },
          { name: "Goodman", expr: "σ_a / S_e + σ_m / S_u = 1 / n" },
          { name: "Utmattningsgräns, stål", expr: "S_e′ ≈ 0,5·S_u", note: "Provstav, för S_u upp till ca 1400 MPa. Korrigera sedan för yta, storlek m.m." },
          { name: "Spänningskoncentration", expr: "σ_max = K_t·σ_nom" },
          { name: "Palmgren–Miner", expr: "Σ nᵢ / Nᵢ = 1 vid brott" },
        ],
        pitfalls: [
          "Att använda materialdatans utmattningsgräns direkt. En verklig detalj med sämre yta och större dimension tål betydligt mindre.",
          "Att lägga en skarp kälradie eller ett gänga precis där momentet är störst.",
          "Aluminium har ingen tydlig utmattningsgräns. Det går till slut sönder även vid låga amplituder.",
        ],
        quiz: [
          { q: "Varför spricker axlar ofta vid en avsats?", a: "Avsatsen ger en spänningskoncentration (Kt). Den lokala spänningen blir mycket högre än den nominella, och där startar utmattningssprickan." },
          { q: "Vad betyder det att en detalj har förbrukat 0,6 enligt Palmgren–Miner?", a: "Att den har använt ungefär 60 % av sin utmattningslivslängd enligt modellen." },
          { q: "En axel belastas med en spänning som varierar mellan −100 och +200 MPa. Vad är medelspänning och amplitud?", a: "σm = (200 − 100)/2 = 50 MPa och σa = (200 + 100)/2 = 150 MPa." },
        ],
        resources: [
          { title: "The Efficient Engineer", kind: "video", url: EFFICIENT, note: "Utmärkta videor om utmattning och brottmekanik" },
          { title: "MIT 2.002 Mechanics and Materials II", kind: "kurs", url: OCW("2-002-mechanics-and-materials-ii-spring-2004"), note: "Brott och utmattning på djupet" },
          { title: "Budynas & Nisbett: Shigley's Mechanical Engineering Design", kind: "bok", note: "Kapitlen om utmattning är standardreferensen" },
        ],
      },
    ],
  },
  {
    id: "termo",
    title: "Termo och flöde",
    topics: [
      {
        id: "termodynamik",
        title: "Termodynamik",
        level: "karna",
        summary: "Energi, värme och arbete: motorer, värmepumpar och kylsystem.",
        why: "Termodynamiken sätter gränserna för all energiomvandling. Den behövs för motorer, kompressorer, värmepumpar och för att förstå verkningsgrader.",
        concepts: [
          "System, tillstånd och processer",
          "Termodynamikens första huvudsats",
          "Ideal gas och tillståndsdiagram",
          "Entalpi och öppna system (stationärt flöde)",
          "Andra huvudsatsen och entropi",
          "Kretsprocesser: Carnot, Rankine, Otto",
          "Kyl- och värmepumpsprocesser, COP",
        ],
        formulas: [
          { name: "Första huvudsatsen (slutet)", expr: "ΔU = Q − W" },
          { name: "Stationärt flöde", expr: "Q̇ − Ẇ = ṁ·(h₂ − h₁ + Δv²/2 + g·Δz)" },
          { name: "Ideal gas", expr: "p·V = m·R·T", note: "Luft: R ≈ 287 J/(kg·K)" },
          { name: "Carnot", expr: "η = 1 − T_C / T_H", note: "Temperaturer i kelvin" },
          { name: "Värmepump", expr: "COP = Q_H / W" },
          { name: "Isentrop, ideal gas", expr: "T₂ / T₁ = (p₂ / p₁)^((κ − 1)/κ)" },
        ],
        pitfalls: [
          "Att räkna med Celsius i kvoter som Carnot eller ideal gas. Använd alltid kelvin.",
          "Att blanda ihop teckenkonventioner för arbete (in eller ut ur systemet).",
        ],
        quiz: [
          { q: "Vilken är den högsta möjliga verkningsgraden för en värmemotor mellan 600 °C och 20 °C?", a: "η = 1 − 293/873 ≈ 66 %." },
          { q: "Hur kan en värmepump leverera mer värme än den el den drar?", a: "Den flyttar värme från en kall källa (luft, mark) till en varm. Elen driver bara flytten, så Q_H = W + Q_C." },
          { q: "Luft komprimeras isentropiskt från 1 bar och 20 °C till 8 bar. Ungefär vilken temperatur får den? (κ = 1,4)", a: "T₂ = 293 · 8 upphöjt till (0,4/1,4) ≈ 293 · 1,81 ≈ 530 K, alltså runt 260 °C." },
        ],
        resources: [
          { title: "MIT: Thermodynamics and Propulsion (anteckningar)", kind: "text", url: "https://web.mit.edu/16.unified/www/FALL/thermodynamics/notes/notes.html", note: "Gratis och genomarbetade kursanteckningar" },
          { title: "Çengel & Boles: Thermodynamics, An Engineering Approach", kind: "bok", note: "Den vanligaste läroboken" },
          { title: "The Efficient Engineer", kind: "video", url: EFFICIENT, note: "Videor om huvudsatserna och kretsprocesser" },
        ],
      },
      {
        id: "stromning",
        title: "Strömningslära",
        level: "karna",
        summary: "Tryck, flöde och förluster i rör, pumpar och runt kroppar.",
        why: "Hydraulik, pneumatik, kylning, pumpar, fläktar och luftmotstånd. Strömningslära finns i nästan varje maskin.",
        concepts: [
          "Hydrostatik och tryck",
          "Kontinuitetsekvationen",
          "Bernoullis ekvation och dess begränsningar",
          "Reynolds tal: laminärt och turbulent",
          "Tryckfall i rör och komponenter",
          "Pumpar och systemkurvor",
          "Luftmotstånd och lyftkraft",
        ],
        formulas: [
          { name: "Hydrostatik", expr: "p = ρ·g·h" },
          { name: "Kontinuitet", expr: "ρ₁·A₁·v₁ = ρ₂·A₂·v₂" },
          { name: "Bernoulli", expr: "p + ½·ρ·v² + ρ·g·z = konst.", note: "Längs en strömlinje, utan förluster" },
          { name: "Reynolds tal", expr: "Re = ρ·v·D / μ", note: "Rörströmning är laminär under ca 2300" },
          { name: "Darcy–Weisbach", expr: "Δp = f·(L/D)·½·ρ·v²", note: "Laminärt: f = 64 / Re" },
          { name: "Luftmotstånd", expr: "F_D = ½·ρ·v²·C_D·A" },
          { name: "Pumpeffekt", expr: "P = Q·Δp / η" },
        ],
        pitfalls: [
          "Att använda Bernoulli genom en pump, ett filter eller långa rör där förlusterna dominerar.",
          "Att glömma att tryckfallet växer ungefär med hastigheten i kvadrat vid turbulent strömning.",
        ],
        quiz: [
          { q: "Vatten strömmar i ett rör som smalnar från 50 till 25 mm i diameter. Hur ändras hastigheten?", a: "Arean blir en fjärdedel, så hastigheten blir fyra gånger så hög." },
          { q: "Hur mycket mer effekt krävs för att övervinna luftmotståndet vid 40 km/h jämfört med 20 km/h?", a: "Kraften ökar med v², effekten med v³: 2³ = 8 gånger så mycket." },
          { q: "Hur stort är vattentrycket på 10 m djup, utöver atmosfärstrycket?", a: "p = ρ·g·h = 1000·9,81·10 ≈ 98 kPa, ungefär 1 bar." },
        ],
        resources: [
          { title: "MIT 2.06 Fluid Dynamics", kind: "kurs", url: OCW("2-06-fluid-dynamics-spring-2013"), note: "Grundkurs i strömningslära" },
          { title: "Practical Engineering", kind: "video", url: "https://www.youtube.com/@PracticalEngineeringChannel", note: "Vatten, dammar och rör i verkligheten" },
          { title: "White: Fluid Mechanics", kind: "bok", note: "Klassisk lärobok" },
          { title: "Engineering ToolBox", kind: "verktyg", url: "https://www.engineeringtoolbox.com", note: "Tabeller för viskositet, rörfriktion m.m." },
        ],
      },
      {
        id: "varme",
        title: "Värmeöverföring",
        level: "fordjup",
        summary: "Ledning, konvektion och strålning: kylning av elektronik, motorer och hus.",
        why: "Allt som utvecklar värme måste kylas, och temperatur påverkar både material, toleranser och livslängd.",
        concepts: [
          "Värmeledning och värmeledningsförmåga",
          "Konvektion och värmeövergångstal",
          "Strålning och emissivitet",
          "Termiska resistanser i serie och parallellt",
          "Flänsar och kylflänsar",
          "Transient uppvärmning (Biots tal)",
          "Termisk utvidgning",
        ],
        formulas: [
          { name: "Ledning", expr: "Q̇ = k·A·ΔT / L" },
          { name: "Konvektion", expr: "Q̇ = h·A·ΔT" },
          { name: "Strålning", expr: "Q̇ = ε·σ·A·(T⁴ − T_omg⁴)" },
          { name: "Termisk resistans", expr: "R_ledn = L/(k·A),  R_konv = 1/(h·A)" },
          { name: "Uppvärmning", expr: "Q = m·c·ΔT" },
          { name: "Biots tal", expr: "Bi = h·L_c / k", note: "Under 0,1: samma temperatur i hela kroppen" },
          { name: "Utvidgning", expr: "ΔL = α·L·ΔT", note: "Stål ca 12·10⁻⁶ /K, aluminium ca 23·10⁻⁶ /K" },
        ],
        pitfalls: [
          "Att glömma att aluminium och stål utvidgar sig olika mycket. Det ger spänningar och glapp i blandade konstruktioner.",
          "Att anta att en kylfläns hjälper om kontaktytan mot värmekällan är dålig. Kontaktresistansen kan dominera.",
        ],
        quiz: [
          { q: "En 1 m lång aluminiumprofil värms från 20 till 70 °C. Hur mycket längre blir den?", a: "ΔL = 23·10⁻⁶·1000·50 ≈ 1,2 mm." },
          { q: "Varför har kylflänsar många tunna flänsar?", a: "För att få stor yta (A) mot luften, eftersom det konvektiva värmeövergångstalet h mot luft är litet." },
          { q: "Hur ändras värmeflödet genom en vägg om du fördubblar isoleringens tjocklek?", a: "Isoleringens resistans fördubblas, så flödet blir nästan hälften. Lite mer än hälften, eftersom övergångsresistanserna vid ytorna är oförändrade." },
        ],
        resources: [
          { title: "Lienhard: A Heat Transfer Textbook", kind: "bok", url: "https://ahtt.mit.edu", note: "Komplett lärobok, gratis som PDF" },
          { title: "MIT 2.51 Intermediate Heat and Mass Transfer", kind: "kurs", url: OCW("2-51-intermediate-heat-and-mass-transfer-fall-2008"), note: "Fördjupningskurs" },
          { title: "Engineering ToolBox", kind: "verktyg", url: "https://www.engineeringtoolbox.com", note: "Värmeledningsförmåga, emissivitet och h-värden" },
        ],
      },
    ],
  },
  {
    id: "material",
    title: "Material",
    topics: [
      {
        id: "material",
        title: "Materiallära och materialval",
        level: "karna",
        summary: "Stål, aluminium, plaster och kompositer: egenskaper, behandling och val.",
        why: "Materialet avgör vikt, kostnad, hållfasthet, korrosion och hur detaljen kan tillverkas. Ett bra materialval löser ofta problem som ingen beräkning kan.",
        concepts: [
          "Dragprovkurvan: E-modul, sträckgräns, brottgräns, brottförlängning",
          "Hårdhet och slagseghet",
          "Stålsorter och beteckningar (t.ex. S355, 42CrMo4)",
          "Värmebehandling: härdning, anlöpning, sätthärdning",
          "Aluminiumlegeringar och tillstånd (t.ex. 6082-T6)",
          "Plaster: termoplaster och härdplaster",
          "Korrosion och galvanisk korrosion",
          "Materialval med Ashbydiagram",
        ],
        formulas: [
          { name: "Specifik styvhet", expr: "E / ρ", note: "Stål, aluminium och titan ligger förvånansvärt lika" },
          { name: "Lätt balk vid given styvhet", expr: "Maximera E^(1/2) / ρ", note: "Ashbys materialindex" },
          { name: "Stål", expr: "E ≈ 210 GPa,  ρ ≈ 7850 kg/m³" },
          { name: "Aluminium", expr: "E ≈ 70 GPa,  ρ ≈ 2700 kg/m³" },
          { name: "Titan", expr: "E ≈ 110 GPa,  ρ ≈ 4500 kg/m³" },
        ],
        pitfalls: [
          "Att byta till ett starkare stål för att få en styvare detalj. Alla stål har ungefär samma E-modul.",
          "Att skruva ihop rostfritt och aluminium i fukt utan isolering: aluminiumet korroderar.",
          "Att glömma att plaster kryper under konstant last, särskilt när det är varmt.",
        ],
        quiz: [
          { q: "Vad betyder 355 i S355?", a: "Minsta sträckgräns i MPa (för tunna godstjocklekar). S står för konstruktionsstål." },
          { q: "En stålbalk böjer sig för mycket. Hjälper det att byta till ett höghållfast stål?", a: "Nej. E-modulen är nästan densamma, så nedböjningen ändras inte. Ändra geometrin, t.ex. höjden, i stället." },
          { q: "Vad betyder T6 i aluminiumlegeringen 6082-T6?", a: "Att materialet är upplösningsbehandlat och varmåldrat, vilket ger legeringens högsta hållfasthet." },
        ],
        resources: [
          { title: "DoITPoMS (University of Cambridge)", kind: "text", url: "https://www.doitpoms.ac.uk", note: "Interaktiva lektioner i materialvetenskap" },
          { title: "Ashby: Materials Selection in Mechanical Design", kind: "bok", note: "Metoden bakom materialval med index och diagram" },
          { title: "Callister: Materials Science and Engineering", kind: "bok", note: "Grundläggande lärobok" },
          { title: "MatWeb", kind: "verktyg", url: "https://www.matweb.com", note: "Databas med materialdata" },
        ],
      },
    ],
  },
  {
    id: "konstruktion",
    title: "Konstruktion",
    topics: [
      {
        id: "maskinelement",
        title: "Maskinelement",
        level: "karna",
        summary: "Skruvförband, lager, axlar, kugghjul, fjädrar och svetsar.",
        why: "De flesta maskiner byggs av standardkomponenter. Att välja och dimensionera dem rätt är vardagen för en konstruktör.",
        concepts: [
          "Skruvförband: förspänning och åtdragningsmoment",
          "Rullningslager: val och livslängd",
          "Axlar och axel–nav-förband (kil, presspassning)",
          "Kugghjul: modul, utväxling och verkningsgrad",
          "Remmar och kedjor",
          "Fjädrar",
          "Svetsförband",
          "Tätningar och smörjning",
        ],
        formulas: [
          { name: "Åtdragningsmoment", expr: "T ≈ K·F·d", note: "K ≈ 0,2 torrt, lägre smort" },
          { name: "Lagerlivslängd", expr: "L₁₀ = (C / P)^p miljoner varv", note: "p = 3 för kullager, 10/3 för rullager" },
          { name: "Utväxling", expr: "i = n₁ / n₂ = z₂ / z₁" },
          { name: "Modul", expr: "m = d / z" },
          { name: "Axeleffekt", expr: "P = T·ω" },
          { name: "Skruvfjäder", expr: "k = G·d⁴ / (8·D³·n)" },
        ],
        pitfalls: [
          "Att dra skruvar till ett moment utan att veta friktionen. Samma moment kan ge mycket olika förspänning.",
          "Att låsa ett lager axiellt i båda ändar av en lång axel: värmeutvidgningen förstör lagren. Ha ett fast och ett fritt lager.",
        ],
        quiz: [
          { q: "Ett kullager klarar C = 20 kN och belastas med P = 2 kN. Vad blir L₁₀?", a: "(20/2)³ = 1000 miljoner varv." },
          { q: "Varför är förspänningen viktig i ett skruvförband som belastas cykliskt?", a: "Med tillräcklig förspänning tar klämda delar det mesta av den varierande lasten, så skruven ser bara en liten spänningsamplitud och håller mot utmattning." },
          { q: "Ett kugghjulspar har z₁ = 20 och z₂ = 60. Drevet går med 1500 varv/min och 30 Nm. Vad blir det på utgående axel, utan förluster?", a: "i = 60/20 = 3, så 500 varv/min och 90 Nm." },
        ],
        resources: [
          { title: "MIT 2.72 Elements of Mechanical Design", kind: "kurs", url: OCW("2-72-elements-of-mechanical-design-spring-2009"), note: "Maskinelement och konstruktionsprojekt" },
          { title: "Budynas & Nisbett: Shigley's Mechanical Engineering Design", kind: "bok", note: "Referensboken för maskinelement" },
          { title: "KHK Gear Knowledge", kind: "text", url: "https://khkgears.net/new/gear_knowledge/", note: "Allt om kugghjul från en tillverkare" },
          { title: "McMaster-Carr", kind: "verktyg", url: "https://www.mcmaster.com", note: "Katalog med data och CAD-modeller för standarddetaljer" },
          { title: "Mekanismer i animation (Nguyen Duc Thang)", kind: "video", url: "https://www.youtube.com/@Thang010146", note: "Tusentals mekanismer som korta animationer" },
        ],
      },
      {
        id: "ritning",
        title: "Ritningar, toleranser och GD&T",
        level: "karna",
        summary: "Att kommunicera en detalj entydigt: mått, passningar och geometriska toleranser.",
        why: "Ritningen är kontraktet med verkstaden. Otydliga eller för snäva toleranser gör detaljer dyra, och för lösa gör att de inte passar.",
        concepts: [
          "Projektion: förstavinkel (Europa) och tredjevinkel (USA)",
          "Måttsättning efter funktion",
          "Allmäntoleranser (ISO 2768)",
          "Passningar enligt ISO 286 (t.ex. H7/g6)",
          "Toleranskedjor: värsta fall och statistiskt",
          "Geometriska toleranser: form, riktning, läge, kast",
          "Referenser (datum) A, B, C",
          "Ytjämnhet (Ra)",
        ],
        formulas: [
          { name: "Toleranskedja, värsta fall", expr: "T = Σ tᵢ" },
          { name: "Toleranskedja, statistiskt", expr: "T = √(Σ tᵢ²)", note: "Rimligt när många mått varierar oberoende" },
          { name: "Spelpassning", expr: "H7/g6", note: "Glidande passning, t.ex. för styrningar" },
          { name: "Presspassning", expr: "H7/p6", note: "Lätt presspassning, t.ex. för lager i nav" },
        ],
        pitfalls: [
          "Att tolerera allt snävt för säkerhets skull. Kostnaden ökar kraftigt med snävare toleranser.",
          "Att måttsätta i kedja när det är avståndet mellan två funktionsytor som spelar roll.",
        ],
        quiz: [
          { q: "Fem mått i en kedja har vardera ±0,1 mm. Vad blir slutmåttets tolerans i värsta fall och statistiskt?", a: "Värsta fall ±0,5 mm. Statistiskt ±√(5·0,1²) ≈ ±0,22 mm." },
          { q: "Vad är skillnaden mellan positionstolerans och en ± på ett hålläge?", a: "Positionstolerans ger en cirkulär toleranszon kring det teoretiskt exakta läget, i stället för en kvadratisk. Den är större för samma funktion och går att koppla till referenser." },
          { q: "Vad betyder passningen H7/g6 i praktiken?", a: "Hålet är aldrig mindre än nominellt mått och axeln alltid lite mindre, så det blir alltid ett litet spel: en glidande passning." },
        ],
        resources: [
          { title: "GD&T Basics", kind: "text", url: "https://www.gdandtbasics.com", note: "Alla symboler förklarade med exempel" },
          { title: "SIS: Svenska institutet för standarder", kind: "verktyg", url: "https://www.sis.se", note: "Här köper du ISO-standarderna (ISO 1101, ISO 286 m.fl.)" },
        ],
      },
      {
        id: "cad",
        title: "CAD och modellering",
        level: "grund",
        summary: "Parametrisk 3D-modellering, sammanställningar och ritningar.",
        why: "CAD är ditt huvudverktyg. Den som modellerar snabbt och robust kan prova fler idéer och ändra utan att allt rasar.",
        concepts: [
          "Fullständigt definierade skisser",
          "Designintention: mått som speglar funktionen",
          "Features och historikträd",
          "Sammanställningar och relationer (mates)",
          "Variabler och konfigurationer",
          "Ritningar från modellen",
          "Versionshantering och export (STEP, STL)",
        ],
        formulas: [],
        pitfalls: [
          "Att referera till kanter som skapats av sena features. När något ändras tappar modellen sina referenser.",
          "Att modellera utan att tänka på hur detaljen ska tillverkas.",
        ],
        quiz: [
          { q: "Varför ska skisser vara fullständigt definierade?", a: "Annars kan geometrin flytta sig oväntat när du ändrar något, och modellen blir svår att förutsäga." },
          { q: "Vad menas med designintention?", a: "Att modellen är uppbyggd så att den ändras som du vill. Mått och relationer speglar funktionen, till exempel att ett hål alltid sitter mitt på en fläns." },
          { q: "Varför skickar man STEP-filer till en verkstad i stället för den egna CAD-filen?", a: "STEP är ett neutralt ISO-format (ISO 10303) som nästan alla CAD- och CAM-program kan läsa, oavsett vilket program du själv använder." },
        ],
        resources: [
          { title: "Onshape Learning Center", kind: "kurs", url: "https://learn.onshape.com", note: "Gratis kurser från grunderna till FeatureScript" },
          { title: "McMaster-Carr", kind: "verktyg", url: "https://www.mcmaster.com", note: "Ladda ned CAD för skruvar, lager och profiler" },
        ],
      },
      {
        id: "produktutveckling",
        title: "Produktutveckling",
        level: "fordjup",
        summary: "Från behov till färdig produkt: krav, koncept, prototyper och verifiering.",
        why: "Tekniken är bara en del. Att förstå kraven, välja rätt koncept och verifiera tidigt avgör om produkten blir lyckad.",
        concepts: [
          "Kravspecifikation",
          "Konceptgenerering och funktionsanalys",
          "Konceptval med Pughmatris",
          "Prototyper och tester",
          "DFMEA: riskanalys av konstruktionen",
          "Konstruktion för montering (DFA) och tillverkning (DFM)",
          "Verifiering och validering",
          "Kostnadsuppskattning",
        ],
        formulas: [
          { name: "Riskprioritetstal (FMEA)", expr: "RPN = S·O·D", note: "Allvarlighet × sannolikhet × upptäckbarhet" },
        ],
        pitfalls: [
          "Att låsa sig vid det första konceptet.",
          "Att testa först när allt är färdigt. Bygg enkla prototyper tidigt.",
        ],
        quiz: [
          { q: "Vad är skillnaden mellan verifiering och validering?", a: "Verifiering: byggde vi produkten rätt, alltså uppfyller den kraven? Validering: byggde vi rätt produkt, alltså löser den användarens behov?" },
          { q: "Hur fungerar en Pughmatris?", a: "Varje koncept jämförs med ett referenskoncept, kriterium för kriterium, med +, 0 eller −. Summan visar vilka koncept som är värda att gå vidare med." },
          { q: "Varför ska man bygga enkla prototyper tidigt?", a: "De avslöjar fel och missförstånd billigt, innan mycket tid har lagts på detaljkonstruktion." },
        ],
        resources: [
          { title: "Ulrich & Eppinger: Product Design and Development", kind: "bok", note: "Standardboken i produktutveckling" },
          { title: "Johannesson, Persson & Pettersson: Produktutveckling", kind: "bok", note: "Svensk lärobok om metoder och verktyg" },
          { title: "MIT 2.007 Design and Manufacturing I", kind: "kurs", url: OCW("2-007-design-and-manufacturing-i-spring-2009"), note: "Konstruera och bygga en robot från idé till tävling" },
        ],
      },
    ],
  },
  {
    id: "tillverkning",
    title: "Tillverkning",
    topics: [
      {
        id: "tillverkning",
        title: "Tillverkningsmetoder",
        level: "karna",
        summary: "Skärande bearbetning, plåt, gjutning, formsprutning, svetsning och 3D-utskrift.",
        why: "En konstruktion som inte går att tillverka billigt är en dålig konstruktion. Du måste veta vad verkstaden kan göra och vad det kostar.",
        concepts: [
          "Svarvning och fräsning (CNC)",
          "Skärdata: skärhastighet, matning, skärdjup",
          "Plåtbearbetning: laser, bockning, K-faktor",
          "Gjutning",
          "Formsprutning av plast: släppvinklar och godstjocklek",
          "Svetsmetoder: MIG/MAG, TIG",
          "Additiv tillverkning: FDM, SLS, metall",
          "Ytbehandling: anodisering, förzinkning, lackering",
        ],
        formulas: [
          { name: "Skärhastighet", expr: "v_c = π·D·n / 1000", note: "m/min, med D i mm och n i varv/min" },
          { name: "Matningshastighet (fräsning)", expr: "v_f = f_z·z·n" },
          { name: "Avverkning", expr: "Q = a_p·a_e·v_f" },
        ],
        pitfalls: [
          "Att rita invändiga hörn med skarpa radier i fräsade fickor. Fräsen är rund.",
          "Att ge formsprutade detaljer varierande godstjocklek. Det ger sjunkmärken och skevhet.",
          "Att lägga hål för nära en bockning i plåt.",
        ],
        quiz: [
          { q: "Vilket varvtal behövs för att fräsa med en Ø10 mm fräs i aluminium med v_c = 300 m/min?", a: "n = 1000·300/(π·10) ≈ 9500 varv/min." },
          { q: "Varför har formsprutade detaljer släppvinklar?", a: "För att detaljen ska släppa ur formen utan att repas eller fastna." },
          { q: "Vilken minsta invändiga hörnradie kan en fräs på Ø6 mm ge i en ficka?", a: "3 mm, alltså fräsens radie. Rita gärna lite större radie, så att fräsen inte går i full omslutning i hörnet." },
        ],
        resources: [
          { title: "Titans of CNC Academy", kind: "kurs", url: "https://academy.titansofcnc.com", note: "Gratis kurser i CNC-bearbetning och CAM" },
          { title: "NYC CNC", kind: "video", url: "https://www.youtube.com/@NYCCNC", note: "Praktisk CNC-bearbetning" },
          { title: "This Old Tony", kind: "video", url: "https://www.youtube.com/@ThisOldTony", note: "Svarv- och fräsarbete med humor" },
          { title: "Kalpakjian & Schmid: Manufacturing Engineering and Technology", kind: "bok", note: "Översikt över alla tillverkningsmetoder" },
        ],
      },
    ],
  },
  {
    id: "analys",
    title: "Analys och system",
    topics: [
      {
        id: "fem",
        title: "FEM och simulering",
        level: "fordjup",
        summary: "Finita elementmetoden: rätt randvillkor, nät och tolkning.",
        why: "FEM ger svar på komplexa geometrier, men fel modell ger snygga färgbilder som är helt fel. Förståelse för metoden är viktigare än knapparna.",
        concepts: [
          "Styvhetsmatris och elementtyper (balk, skal, solid)",
          "Randvillkor och laster",
          "Nätkonvergens",
          "Singulariteter: spänningar som aldrig konvergerar",
          "Linjär och olinjär analys",
          "Modalanalys (egenfrekvenser)",
          "Kontrollera mot handberäkning",
        ],
        formulas: [
          { name: "Grundekvationen", expr: "K·u = F" },
          { name: "Modalanalys", expr: "(K − ω²·M)·φ = 0" },
        ],
        pitfalls: [
          "Att lita på spänningen i ett skarpt hörn eller vid en punktlast. Den växer bara när nätet förfinas.",
          "Att låsa modellen hårdare än verkligheten. Det ger för styva svar.",
          "Att aldrig kontrollera reaktionskrafterna mot de pålagda lasterna.",
        ],
        quiz: [
          { q: "Spänningen i ett skarpt invändigt hörn ökar varje gång du förfinar nätet. Vad betyder det?", a: "Det är en singularitet. Modellen saknar radie, så spänningen är teoretiskt oändlig. Lägg in den verkliga radien eller bedöm spänningen en bit bort." },
          { q: "Hur vet du att nätet är tillräckligt fint?", a: "Gör en konvergensstudie: förfina nätet där du läser av resultatet och kontrollera att värdet slutar ändras." },
          { q: "Varför ska man kontrollera reaktionskrafterna?", a: "De ska balansera de pålagda lasterna. Gör de inte det är laster eller randvillkor fel." },
        ],
        resources: [
          { title: "MIT 2.092 Finite Element Analysis of Solids and Fluids I", kind: "kurs", url: OCW("2-092-finite-element-analysis-of-solids-and-fluids-i-fall-2009"), note: "Klaus-Jürgen Bathes kurs" },
          { title: "SimScale: What is FEA?", kind: "text", url: "https://www.simscale.com/docs/simwiki/fea-finite-element-analysis/what-is-fea-finite-element-analysis/", note: "Kort introduktion till metoden" },
          { title: "The Efficient Engineer", kind: "video", url: EFFICIENT, note: "Bra video om hur FEM fungerar" },
        ],
      },
      {
        id: "reglerteknik",
        title: "Reglerteknik och mekatronik",
        level: "fordjup",
        summary: "Återkoppling, PID och stabilitet: få mekanik att göra som du vill.",
        why: "Moderna maskiner styrs av motorer, sensorer och mjukvara. En maskiningenjör behöver förstå reglerloopen för att konstruera mekaniken rätt.",
        concepts: [
          "Öppen och sluten loop",
          "Överföringsfunktioner och Laplacetransform",
          "Systemsvar: tidskonstant, översläng, insvängningstid",
          "PID-regulatorn och intrimning",
          "Stabilitet, poler och stabilitetsmarginaler",
          "Bodediagram",
          "Motorer, givare och drivsystem",
        ],
        formulas: [
          { name: "PID", expr: "u = K_p·e + K_i·∫e dt + K_d·de/dt" },
          { name: "Första ordningens system", expr: "G(s) = K / (τ·s + 1)", note: "Når 63 % av slutvärdet efter tiden τ" },
          { name: "Sluten loop", expr: "G_sl = G / (1 + G·H)" },
          { name: "Stabilitet", expr: "Alla poler i vänster halvplan" },
        ],
        pitfalls: [
          "Att försöka reglera bort en vek eller glappig mekanik. Styv mekanik gör regleringen enkel.",
          "Att höja förstärkningen tills det svänger utan att förstå varför.",
        ],
        quiz: [
          { q: "Vad gör I-delen i en PID-regulator?", a: "Den integrerar felet över tid och tar bort det kvarstående felet, men för stor I-del ger översläng och svängningar." },
          { q: "Ett första ordningens system har τ = 2 s. Hur lång tid tar det ungefär att nå slutvärdet efter ett steg?", a: "Ungefär 4–5 tidskonstanter, alltså 8–10 s. Då är man på 98–99 % av slutvärdet." },
          { q: "Vad händer om förstärkningen i en P-regulator är för hög?", a: "Systemet blir snabbt men får översläng och svängningar, och kan till slut bli instabilt." },
        ],
        resources: [
          { title: "Brian Douglas", kind: "video", url: "https://www.youtube.com/@BrianBDouglas", note: "Den bästa intuitiva introduktionen till reglerteknik" },
          { title: "MIT 2.14 Analysis and Design of Feedback Control Systems", kind: "kurs", url: OCW("2-14-analysis-and-design-of-feedback-control-systems-spring-2014"), note: "Komplett kurs" },
          { title: "Steve Brunton", kind: "video", url: "https://www.youtube.com/@Eigensteve", note: "Control Bootcamp och datadriven reglering" },
          { title: "MIT 2.12 Introduction to Robotics", kind: "kurs", url: OCW("2-12-introduction-to-robotics-fall-2005"), note: "Kinematik och styrning av robotar" },
        ],
      },
      {
        id: "matteknik",
        title: "Mätteknik",
        level: "fordjup",
        summary: "Mät rätt: noggrannhet, osäkerhet, givare och datainsamling.",
        why: "Utan bra mätningar kan du inte verifiera dina beräkningar. Mätteknik behövs både i labbet och i kvalitetskontrollen.",
        concepts: [
          "Noggrannhet, precision och upplösning",
          "Mätosäkerhet och hur den kombineras",
          "Skjutmått, mikrometer och koordinatmätmaskin",
          "Trådtöjningsgivare och Wheatstonebrygga",
          "Lastceller, termoelement och accelerometrar",
          "Sampling och vikning (aliasing)",
          "Kalibrering och spårbarhet",
        ],
        formulas: [
          { name: "Kombinerad osäkerhet", expr: "u_c = √(Σ uᵢ²)", note: "För oberoende bidrag" },
          { name: "Töjningsgivare", expr: "ΔR / R = k·ε", note: "Givarfaktorn k ≈ 2" },
          { name: "Samplingsteoremet", expr: "f_s > 2·f_max" },
        ],
        pitfalls: [
          "Att tro att många decimaler betyder hög noggrannhet.",
          "Att sampla för långsamt och få fram frekvenser som inte finns.",
        ],
        quiz: [
          { q: "Två oberoende osäkerheter är 3 µm och 4 µm. Vad blir den kombinerade?", a: "√(3² + 4²) = 5 µm." },
          { q: "En signal på 300 Hz samplas med 400 Hz. Vad ser du i mätdatan?", a: "En falsk signal på 100 Hz på grund av vikning, eftersom samplingsfrekvensen är lägre än 2 · 300 Hz." },
          { q: "Vad är skillnaden mellan noggrannhet och precision?", a: "Noggrannhet är hur nära det sanna värdet du mäter. Precision är hur lika upprepade mätningar blir, även om de alla är fel." },
        ],
        resources: [
          { title: "Figliola & Beasley: Theory and Design for Mechanical Measurements", kind: "bok", note: "Standardboken i mätteknik" },
          { title: "NPTEL", kind: "kurs", url: "https://nptel.ac.in", note: "Gratis indiska universitetskurser, sök på mechanical measurements" },
        ],
      },
      {
        id: "programmering",
        title: "Programmering och numerik",
        level: "grund",
        summary: "Python eller MATLAB för beräkningar, simulering och databehandling.",
        why: "Att kunna skriva ett kort skript sparar timmar: parameterstudier, mätdata, optimering och simulering av det som inte har en analytisk lösning.",
        concepts: [
          "Grundläggande Python: variabler, loopar, funktioner",
          "NumPy och matriser",
          "Plotta med matplotlib",
          "Lösa ekvationer numeriskt (Newton–Raphson)",
          "Lösa ODE numeriskt (Euler, Runge–Kutta)",
          "Kurvanpassning med minsta kvadrat",
          "Läsa och bearbeta mätdata",
        ],
        formulas: [
          { name: "Newton–Raphson", expr: "x_{n+1} = x_n − f(x_n) / f′(x_n)" },
          { name: "Eulers metod", expr: "y_{n+1} = y_n + h·f(t_n, y_n)" },
          { name: "Minsta kvadrat", expr: "min Σ (yᵢ − f(xᵢ))²" },
        ],
        pitfalls: [
          "Att använda för stort tidssteg i en simulering. Halvera steget och se om svaret ändras.",
          "Att göra om samma beräkning i Excel varje gång i stället för att skriva ett skript.",
        ],
        quiz: [
          { q: "Hur kontrollerar du att en numerisk ODE-lösning är tillräckligt noggrann?", a: "Minska tidssteget, till exempel halvera det, och kontrollera att resultatet inte ändras nämnvärt." },
          { q: "Varför är Newton–Raphson snabb, och när misslyckas den?", a: "Nära roten fördubblas antalet rätta siffror för varje steg. Den kan misslyckas med en dålig startgissning eller där derivatan är nära noll." },
          { q: "Hur löser du A·x = b i NumPy?", a: "Med numpy.linalg.solve(A, b). Det är snabbare och noggrannare än att först invertera A." },
        ],
        resources: [
          { title: "Python Tutorial", kind: "text", url: "https://docs.python.org/3/tutorial/", note: "Officiell introduktion till Python" },
          { title: "NumPy: Learn", kind: "text", url: "https://numpy.org/learn/", note: "Kom igång med NumPy" },
          { title: "MIT 2.086 Numerical Computation for Mechanical Engineers", kind: "kurs", url: OCW("2-086-numerical-computation-for-mechanical-engineers-fall-2014"), note: "Numerik med ingenjörsexempel" },
        ],
      },
    ],
  },
  {
    id: "yrket",
    title: "Yrket",
    topics: [
      {
        id: "standarder",
        title: "Standarder, säkerhet och CE",
        level: "fordjup",
        summary: "Maskindirektivet, riskbedömning och standarder som styr arbetet.",
        why: "En maskin som säljs i EU måste vara säker och CE-märkt. Konstruktören ansvarar för att riskerna är bedömda och åtgärdade.",
        concepts: [
          "Maskindirektivet 2006/42/EG och nya maskinförordningen (EU) 2023/1230",
          "CE-märkning och teknisk dokumentation",
          "Riskbedömning enligt EN ISO 12100",
          "Funktionssäkerhet: EN ISO 13849 (PL)",
          "Harmoniserade standarder",
          "Kvalitetssystem: ISO 9001",
          "Ritningsstandarder: ISO 128 och ISO 1101",
        ],
        formulas: [],
        pitfalls: [
          "Att göra riskbedömningen i efterhand. Den ska styra konstruktionen.",
          "Att glömma att maskinförordningen ersätter maskindirektivet från 20 januari 2027.",
        ],
        quiz: [
          { q: "I vilken ordning ska risker åtgärdas enligt EN ISO 12100?", a: "1. Konstruera bort risken. 2. Skydd och skyddsanordningar. 3. Information och varningar." },
          { q: "Vad är en harmoniserad standard?", a: "En standard som EU har pekat ut. Följer du den antas din maskin uppfylla de krav i lagen som standarden täcker." },
          { q: "Vad anger PL i EN ISO 13849?", a: "Performance Level, a till e: hur tillförlitligt en säkerhetsfunktion i styrsystemet fungerar. PL e är högst." },
        ],
        resources: [
          { title: "Maskinförordningen (EU) 2023/1230", kind: "text", url: "https://eur-lex.europa.eu/eli/reg/2023/1230/oj", note: "Lagtexten på EUR-Lex" },
          { title: "SIS: Svenska institutet för standarder", kind: "verktyg", url: "https://www.sis.se", note: "Köp och sök standarder" },
        ],
      },
    ],
  },
];

export const TOPICS: Topic[] = AREAS.flatMap((a) => a.topics);
export const topicById = (id: string) => TOPICS.find((t) => t.id === id);
